/**
 * Modal simplificado para que el cliente (propietario) cree o edite sus
 * propios bloqueos / mantenciones.
 *
 * Modelo de fechas (ver docs/calendario-logica.md §14 — modelo día de
 * llegada → día de salida):
 *   El usuario elige DÍAS en el RangePicker, no noches.
 *     - rango[0] = día de llegada (check-in 15:00).
 *     - rango[1] = día de salida  (check-out 10:59 esa misma mañana).
 *     - noches   = rango[1] - rango[0] (diferencia de días).
 *   Ejemplo: [7, 9] ⇒ entra 7 15:00, sale 9 10:59, ocupa las noches del
 *   7→8 y del 8→9 = 2 noches.
 *   Por tanto el mínimo válido es rango[0] != rango[1] (mínimo 1 noche).
 *
 * Reglas de negocio impuestas aquí:
 *  - Horario fijo: check-in 15:00 del día de llegada · check-out 10:59
 *    del día de salida. El cliente nunca elige horas ni tercios.
 *  - Sólo día de llegada y día de salida son editables.
 *  - Razón obligatoria; notas admin-only.
 *  - Advertencia clara: para llegar antes de las 15:00 hay que bloquear
 *    el día anterior; para irse después de las 11:00 hay que bloquear
 *    un día extra al final. Los "antes de" / "después de" son sólo
 *    etiquetas humanas de referencia — NUNCA se persiste 11:00 como
 *    `hora_termino` (ver MD §1.2).
 *  - Solo se permite un único bloqueo contiguo sin superposición con
 *    aseos, arriendos u otros bloqueos. La validación contra el estado
 *    actual del calendario se hace en el caller (`calendarioClienteMonth`).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  HomeOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import type { CreatePayload } from "./calendarCreateModal";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ahoraChile } from "../../utils/fechaChile";

const ISO_FMT = "YYYY-MM-DD";

// Horas fijas del cliente (ver docs/calendario-logica.md §1.2):
//  - CHECKIN: primer minuto de `evening` (inclusive).
//  - CHECKOUT: último minuto de `morning` (inclusive) = 10:59. Persistir
//    `11:00` sería violar la convención de "último minuto ocupado".
const CHECKIN_TIME = "15:00";
const CHECKOUT_TIME = "10:59";
// Hora borde humana para textos de banner / referencia.
// Canónicamente: CHECKOUT_TIME + 1 minuto = "11:00" = inicio del tercio
// siguiente. Solo para redacción; NUNCA se persiste.
const CHECKOUT_BOUNDARY_LABEL = "11:00";

type Mode = "create" | "edit";

export type ClientBlockInitial = {
  id?: number | string;
  kind?: "block" | "maintenance";
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD (último día ocupado)
  razon?: string;
  notas?: string;
};

type Props = {
  open: boolean;
  mode: Mode;
  propertyId: number | null;
  propertyName: string | null;
  initial?: ClientBlockInitial | null;
  onClose: () => void;
  onSubmit: (payload: CreatePayload) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  /** Valida que el rango elegido no pise nada del calendario. */
  validateNoOverlap: (startISO: string, endISO: string) => string | null;
  /**
   * ¿El día (YYYY-MM-DD) tiene los 3 tercios ocupados por eventos AJENOS al
   * bloqueo en edición? Si sí, el RangePicker lo deshabilita — no hay dónde
   * poner el bloqueo. El propio bloqueo en edición NO cuenta (el caller ya
   * excluye su id). Compartir un día parcialmente sigue permitido. Round
   * 2026-07-03 — paridad con el date-picker del admin.
   */
  isDayFullyOccupied?: (iso: string) => boolean;
};

const { Text } = Typography;

export default function ClientBlockModal({
  open,
  mode,
  propertyId,
  propertyName,
  initial,
  onClose,
  onSubmit,
  onDelete,
  validateNoOverlap,
  isDayFullyOccupied,
}: Props) {
  const [form] = Form.useForm();
  // Guard anti doble-submit (ver comentario equivalente en CalendarCreateModal).
  // Sin esto, un cliente clickeando rápido "Crear" creaba varios bloqueos
  // idénticos antes de que el POST terminara.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      // Modelo día-a-día: initial.startDate = día de llegada,
      // initial.endDate = día de salida (check-out, tal cual lo persiste
      // el backend). Sin restas ni sumas: los mostramos directo en el
      // RangePicker como [arrival, departure].
      const arrival = initial.startDate ? dayjs(initial.startDate) : ahoraChile();
      const departure = initial.endDate
        ? dayjs(initial.endDate)
        : arrival.add(1, "day");
      // El cliente NO ve ni edita `notas` (campo admin-only).
      form.setFieldsValue({
        kind: initial.kind ?? "block",
        rango: [
          arrival,
          departure.isAfter(arrival, "day") ? departure : arrival.add(1, "day"),
        ],
        razon: initial.razon ?? "",
      });
      return;
    }
    // create: pre-cargar con initial?.startDate como día de llegada; el
    // día de salida default es arrival + 1 (mínimo 1 noche).
    form.resetFields();
    const arrival = initial?.startDate ? dayjs(initial.startDate) : ahoraChile();
    form.setFieldsValue({
      kind: "block",
      rango: [arrival, arrival.add(1, "day")],
      razon: "",
    });
  }, [open, mode, initial, form]);

  // Título del modal.
  //  - Edit: específico al tipo que se está editando (bloqueo / mantención).
  //  - Create: genérico "bloqueo o mantención" porque el usuario aún no
  //    eligió con el Segmented (arranca en "block" pero puede cambiar).
  const titleText = useMemo(() => {
    if (mode === "edit") {
      const label =
        (initial?.kind ?? "block") === "maintenance"
          ? "mantención"
          : "bloqueo";
      return `Editar ${label}`;
    }
    return "Nuevo bloqueo o mantención";
  }, [mode, initial]);

  const handleOk = async () => {
    // Guard anti doble-submit: ignora clicks adicionales mientras ya se
    // está procesando. Redundante con confirmLoading pero defensivo.
    if (submitting) return;
    if (!propertyId) return;
    try {
      const values = await form.validateFields();
      const [startD, endD] = (values.rango ?? []) as [Dayjs, Dayjs];
      if (!startD || !endD) {
        form.setFields([{ name: "rango", errors: ["Fechas requeridas"] }]);
        return;
      }
      if (startD.isAfter(endD, "day")) {
        form.setFields([
          { name: "rango", errors: ["La fecha final no puede ser anterior"] },
        ]);
        return;
      }

      // Regla cliente: los bloqueos / mantenciones deben cubrir al menos
      // UNA noche. En el modelo día-a-día la noche vive ENTRE el día de
      // llegada y el día de salida; por tanto mínimo 1 noche ⇔
      // `rango[0] != rango[1]` (día de llegada distinto al día de
      // salida). Si arrival == departure el bloqueo serían 0 noches
      // (entrada y salida mismo día), que no tiene sentido.
      if (startD.isSame(endD, "day")) {
        form.setFields([
          {
            name: "rango",
            errors: [
              "El bloqueo debe cubrir al menos una noche — el día de salida debe ser posterior al día de llegada.",
            ],
          },
        ]);
        return;
      }

      // Regla cliente: no se puede crear un bloqueo en fechas pasadas. En
      // edit-mode permitimos que la fecha inicial original ya esté en el
      // pasado (no obligamos a mover lo que ya fue), pero sí exigimos que
      // el rango completo no quede más atrás que el ya persistido.
      const today = ahoraChile().startOf("day");
      const originalStart =
        mode === "edit" && initial?.startDate
          ? dayjs(initial.startDate).startOf("day")
          : null;
      const floor = originalStart && originalStart.isBefore(today) ? originalStart : today;
      if (startD.isBefore(floor, "day")) {
        form.setFields([
          {
            name: "rango",
            errors: ["No puedes crear un bloqueo en fechas pasadas"],
          },
        ]);
        return;
      }

      // Modelo día-a-día:
      //   arrivalISO   = startD = día de llegada (check-in 15:00)
      //   departureISO = endD   = día de salida  (check-out 10:59)
      //   noches       = endD - startD  (sin +1)
      // Ejemplo: [7,9] ⇒ entra 7, sale 9, noches = 2 (7→8 y 8→9).
      const arrivalISO = startD.format(ISO_FMT);
      const departureISO = endD.format(ISO_FMT);
      const noches = endD.diff(startD, "day");

      const overlap = validateNoOverlap(arrivalISO, departureISO);
      if (overlap) {
        form.setFields([{ name: "rango", errors: [overlap] }]);
        return;
      }

      // Payload compatible con CalendarCreateModal.buildPayload — horas
      // fijas (15:00 primer minuto de evening del día de llegada · 10:59
      // último minuto de morning del día de salida) sin que el usuario
      // pueda tocarlas. Ver docs/calendario-logica.md §14 (modelo día-a-día)
      // y §1.2 (convención "último minuto ocupado").
      const payload: CreatePayload = {
        kind: values.kind ?? "block",
        id: initial?.id,
        action: mode === "edit" ? "edicion" : "creacion",
        propertyId,
        startDate: arrivalISO,
        endDate: departureISO,
        startThird: "evening",
        // endThird="morning": 10:59 pertenece a morning (< 11:00).
        endThird: "morning",
        startTime: CHECKIN_TIME,
        endTime: CHECKOUT_TIME,
        startISO: `${arrivalISO}T${CHECKIN_TIME}:00`,
        endISO: `${departureISO}T${CHECKOUT_TIME}:00`,
        razon: values.razon?.trim(),
        // Cliente no envía notas (campo admin-only). Dejamos explícitamente
        // undefined para que el backend no lo sobrescriba si ya existe.
        notas: undefined,
        noches,
      } as CreatePayload;

      // Desde aquí disparamos la llamada al backend. Botón OK queda
      // en loading; finally garantiza reset tanto en éxito como en error.
      setSubmitting(true);
      try {
        await onSubmit(payload);
      } finally {
        setSubmitting(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        // AntD ya pinta los errores en los campos correspondientes.
        return;
      }
      console.error("[ClientBlockModal] error en handleOk", err);
    }
  };

  // Watch rango reactively so el resumen (noches, check-in / check-out) se
  // recalcula en vivo cuando el usuario cambia las fechas.
  const rangoValue = Form.useWatch("rango", form) as
    | [Dayjs, Dayjs]
    | undefined;
  const kindValue = (Form.useWatch("kind", form) ?? "block") as
    | "block"
    | "maintenance";

  const summary = useMemo(() => {
    if (!rangoValue || !rangoValue[0] || !rangoValue[1]) return null;
    const [arrival, departure] = rangoValue;
    // En el modelo día-a-día, si arrival == departure hay 0 noches (caso
    // inválido que el handleOk rechaza). Mostramos resumen sólo cuando
    // hay al menos 1 noche real.
    if (!departure.isAfter(arrival, "day")) return null;
    const noches = departure.diff(arrival, "day");
    return {
      noches,
      checkInLabel: `${arrival.format("DD MMM YYYY")} · ${CHECKIN_TIME}`,
      checkOutLabel: `${departure.format("DD MMM YYYY")} · ${CHECKOUT_TIME}`,
    };
  }, [rangoValue]);

  return (
    <Modal
      title={
        <Space size={8}>
          {kindValue === "maintenance" ? (
            <ToolOutlined style={{ color: "var(--violet-600, #7c3aed)" }} />
          ) : (
            <UserOutlined style={{ color: "var(--orange-500, #f97316)" }} />
          )}
          <span>{titleText}</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      // Nota: el footer de este modal usa BOTONES EXPLÍCITOS con onClick
      // directo a `handleOk` en vez del render-prop `OkBtn`. El render-prop
      // dejó de disparar en algunos stacks (reporte: "el botón Crear no hace
      // nada"), probablemente por cómo AntD 5.24 inyecta onOk dentro de
      // footers customizados con layout split (izquierda Eliminar, derecha
      // Cancel/Ok). Con un Button nativo el comportamiento es determinista.
      maskClosable={!submitting}
      keyboard={!submitting}
      width={580}
      destroyOnClose
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div>
            {mode === "edit" && onDelete ? (
              <Popconfirm
                title="¿Eliminar este bloqueo?"
                description="Esta acción no se puede deshacer."
                okText="Sí, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                placement="topLeft"
                onConfirm={onDelete}
                disabled={submitting}
              >
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={submitting}
                >
                  Eliminar
                </Button>
              </Popconfirm>
            ) : null}
          </div>
          <Space>
            <Button onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="primary" onClick={handleOk} loading={submitting}>
              {mode === "edit" ? "Guardar cambios" : "Crear"}
            </Button>
          </Space>
        </div>
      }
    >
      {propertyName ? (
        <div className="client-block-property-pill">
          <HomeOutlined />
          <Text strong>{formatPropertyName(propertyName)}</Text>
        </div>
      ) : null}

      <Alert
        type="info"
        showIcon
        icon={<ClockCircleOutlined />}
        message={
          <Space direction="vertical" size={2}>
            <Text strong>
              Horario fijo: check-in {CHECKIN_TIME} del día de llegada y
              check-out {CHECKOUT_TIME} del día de salida
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Puedes elegir cualquier rango de días (desde hoy en adelante). Lo
              único que validamos es que tu bloqueo no se superponga{" "}
              <strong>en horas</strong> con otros eventos — compartir el día
              con un aseo o con el check-out/in de otra estadía está permitido
              mientras los horarios no choquen.
            </Text>
          </Space>
        }
        style={{ marginBottom: 12 }}
      />

      {/*
       * Aviso destacado: el usuario subestima que check-in y check-out
       * son HORAS FIJAS. Si quiere llegar temprano o irse tarde, tiene
       * que bloquear un día extra — lo ponemos en un Alert `warning`
       * separado y con tipografía propia para que siempre lo lea.
       */}
      <Alert
        type="warning"
        showIcon
        message={
          <Text strong style={{ fontSize: 13 }}>
            Importante sobre las horas
          </Text>
        }
        description={
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            <li>
              Si necesitas <strong>llegar antes</strong> de las{" "}
              {CHECKIN_TIME}, <strong>bloquea también el día anterior</strong>.
            </li>
            <li>
              Si necesitas <strong>irte después</strong> de las{" "}
              {CHECKOUT_BOUNDARY_LABEL},{" "}
              <strong>bloquea un día extra al final</strong>.
            </li>
          </ul>
        }
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="kind"
          label="Tipo"
          initialValue="block"
          rules={[{ required: true, message: "Selecciona un tipo" }]}
          tooltip="Bloqueo = uso personal tuyo. Mantención = reparación / limpieza profunda."
        >
          <Segmented
            block
            disabled={mode === "edit"}
            options={[
              {
                label: (
                  <span>
                    <UserOutlined /> Bloqueo personal
                  </span>
                ),
                value: "block",
              },
              {
                label: (
                  <span>
                    <ToolOutlined /> Mantención
                  </span>
                ),
                value: "maintenance",
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="rango"
          label="Fechas (día de entrada → día de salida)"
          // Validadores inline con trigger "onChange" — AntD evalúa cada
          // rule al cambiar el valor, así el usuario ve el error al
          // instante en lugar de recién al hacer click en Crear. El check
          // de "al menos 1 noche" es la regla clave: arrival !== departure.
          validateTrigger={["onChange", "onBlur"]}
          rules={[
            { required: true, message: "Selecciona el rango de fechas" },
            {
              validator: (_: unknown, value: [Dayjs, Dayjs] | undefined) => {
                if (!Array.isArray(value) || value.length !== 2)
                  return Promise.resolve();
                const [s, e] = value;
                if (!s || !e) return Promise.resolve();
                if (s.isAfter(e, "day")) {
                  return Promise.reject(
                    new Error("La fecha de salida no puede ser anterior."),
                  );
                }
                if (s.isSame(e, "day")) {
                  return Promise.reject(
                    new Error(
                      "El bloqueo debe cubrir al menos una noche — el día de salida debe ser posterior al día de llegada.",
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          tooltip="Entras el día de llegada a las 15:00 y sales el día de salida a las 10:59. Las noches bloqueadas son las que quedan entremedio: p. ej., del 7 al 9 son 2 noches (la del 7 y la del 8)."
        >
          <DatePicker.RangePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            allowClear={false}
            inputReadOnly
            // El cliente solo puede crear o modificar bloqueos desde hoy
            // en adelante. En modo edit permitimos la fecha inicial del
            // bloqueo existente aunque ya haya pasado (para no obligar al
            // usuario a cancelarlo cuando sólo quiere corregir la razón).
            disabledDate={(current) => {
              if (!current) return false;
              const today = ahoraChile().startOf("day");
              // 1) Fechas pasadas: en edit permitimos la fecha inicial ya
              //    persistida aunque sea pasada; en create, solo hoy en adelante.
              if (mode === "edit" && initial?.startDate) {
                const originalStart = dayjs(initial.startDate).startOf("day");
                const floor = originalStart.isBefore(today)
                  ? originalStart
                  : today;
                if (current.isBefore(floor, "day")) return true;
              } else if (current.isBefore(today, "day")) {
                return true;
              }
              // 2) Días con los 3 tercios ocupados por OTROS eventos (no el
              //    propio bloqueo en edición): no hay dónde poner el bloqueo →
              //    deshabilitados, igual que en el date-picker del admin. Así el
              //    cliente puede reencuadrar SU bloqueo pero no encimarlo sobre
              //    otro bloqueo, arriendo o aseo. Compartir un día parcialmente
              //    sigue permitido (solo se bloquea si NO queda tercio libre).
              if (isDayFullyOccupied?.(current.format(ISO_FMT))) return true;
              return false;
            }}
          />
        </Form.Item>

        {summary ? (
          <div className="client-block-summary">
            <div className="client-block-summary-row">
              <span className="client-block-summary-label">Check-in</span>
              <span className="client-block-summary-value">
                {summary.checkInLabel}
              </span>
            </div>
            <div className="client-block-summary-row">
              <span className="client-block-summary-label">Check-out</span>
              <span className="client-block-summary-value">
                {summary.checkOutLabel}
              </span>
            </div>
            <div className="client-block-summary-row">
              <span className="client-block-summary-label">Total</span>
              <Tag color="geekblue" style={{ margin: 0 }}>
                {summary.noches} {summary.noches === 1 ? "noche" : "noches"}
              </Tag>
            </div>
          </div>
        ) : null}

        <Form.Item
          name="razon"
          label="Razón"
          rules={[
            { required: true, message: "Ingresa una razón" },
            { max: 200, message: "Máximo 200 caracteres" },
          ]}
          tooltip="Este texto es visible para todos los administradores de la propiedad."
        >
          <Input
            placeholder={
              kindValue === "maintenance"
                ? "Ej: pintura, arreglo eléctrico, limpieza profunda..."
                : "Ej: uso personal, visita familiar, vacaciones..."
            }
            maxLength={200}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
